import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { StatCard } from '@/components/StatCard.jsx';
import { formatLKR, periodLabel } from '@/lib/format.js';
import { Users, Wallet, FileText, AlertCircle, Banknote, ShieldCheck } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from 'recharts';
import { Button } from '@/components/ui/Button.jsx';

const COLORS = ['#0284c7', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316'];

export default function DashboardPage() {
  const { data, isLoading } = useQuery({ queryKey: ['dashboard'], queryFn: () => api.get('/dashboard/summary') });

  if (isLoading || !data) return <div className="p-4">Loading…</div>;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Dashboard"
        description={data.lastRun ? `Last payroll: ${periodLabel(data.lastRun.period_year, data.lastRun.period_month)} (${data.lastRun.status})` : 'No payroll runs yet'}
        actions={<Button asChild><Link to="/payroll">Go to Payroll</Link></Button>}
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Active Employees" value={data.activeEmp} hint={`Total: ${data.totalEmp}`} icon={Users} accent="brand" />
        <StatCard label="Last Payroll Cost" value={formatLKR(data.lastRunCost)} icon={Wallet} accent="success" />
        <StatCard label="EPF Liability (last)" value={formatLKR(data.epfLiability)} icon={ShieldCheck} accent="info" />
        <StatCard label="ETF Liability (last)" value={formatLKR(data.etfLiability)} icon={Banknote} accent="warning" />
        <StatCard label="Pending Leave Requests" value={data.pendingLeaves} icon={AlertCircle} accent="warning" />
        <StatCard label="Loans Outstanding" value={formatLKR(data.loansOutstanding)} icon={FileText} accent="danger" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Section title="6-Month Payroll Trend" className="lg:col-span-2">
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={data.trend.map((t) => ({ ...t, costRupees: t.cost / 100 }))}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="label" />
                <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip formatter={(v) => formatLKR(v * 100)} />
                <Bar dataKey="costRupees" fill="#0284c7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section title="Headcount by Department">
          <div className="h-72">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.deptDist} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {data.deptDist.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>
    </div>
  );
}
